import { makeProviderKey } from '@littlebird/integrations';
import { toast } from 'sonner';

import { useDisconnectIntegrationMutation } from '@/features/integrations/integrations.query';
import type {
  CachedConnectedAccount,
  IntegrationState,
} from '@/features/integrations/integrations.types';
import { integrationManagementSearchId } from '@/features/settings/settings-search-dom';
import { useHashRoute } from '@/hooks/useHashRoute';
import { connectAppleNativeToolkit } from '@/integrations/apple-native-connect';
import {
  handleOpenContactsSettings,
  connectOAuthIntegration,
} from '@/integrations/integration-handlers';
import { integrationsStore } from '@/integrations/integrations.store';
import {
  trackOnDeviceConnectClicked,
  trackToolkitConnected,
} from '@/integrations/integrations.utils';
import { terminalIntegrationStore } from '@/integrations/terminal-integration.store';

import type { OAuthReturnTo } from '@littlebird/mcp';

/** Where the Terminal opt-in lives; connect routes here rather than flipping
 * the switch, so the implications are read before it is turned on. */
const TERMINAL_SETTINGS_HASH = `settings/Integrations/${integrationManagementSearchId(
  makeProviderKey('device', 'terminal')
)}`;

type Source = 'settings' | 'integrations_modal';

type IntegrationAction = 'connect' | 'disconnect';

export type HandleIntegrationAction = (
  integration: IntegrationState,
  action: IntegrationAction,
  account?: CachedConnectedAccount
) => Promise<void>;

export function useIntegrationActions(
  source: Source,
  options?: { returnTo?: OAuthReturnTo }
) {
  const disconnectMutation = useDisconnectIntegrationMutation();
  const { setHash } = useHashRoute();

  const handleAction: HandleIntegrationAction = async (
    integration,
    action,
    account
  ) => {
    const { toolkit_name: toolkitName, display_name: displayName } =
      integration.info;

    if (integrationsStore.isPending(toolkitName)) return;

    if (toolkitName === 'terminal') {
      if (action === 'connect') {
        // Enabling is the settings page's job: it carries the acknowledgement
        // of what the agent may then do. A card's + only takes you there.
        trackOnDeviceConnectClicked(toolkitName, 'builtin', source);
        setHash(TERMINAL_SETTINGS_HASH);
        return;
      }
      integrationsStore.markPending(toolkitName);
      try {
        await terminalIntegrationStore.setEnabled(false);
      } catch (error) {
        console.error('Unexpected error in Terminal action:', error);
        toast.error(`Unexpected error: ${displayName}`);
      } finally {
        integrationsStore.unmarkPending(toolkitName);
      }
      return;
    }

    if (toolkitName === 'applereminders' || toolkitName === 'applecontacts') {
      if (action === 'disconnect') {
        if (toolkitName === 'applereminders') {
          window.mainProcessApi.openEventKitSettings('reminders');
        } else {
          handleOpenContactsSettings();
        }
        return;
      }
      await connectAppleNativeToolkit(toolkitName, displayName, source);
      return;
    }

    if (action === 'connect') {
      integrationsStore.markPending(toolkitName);
      try {
        const result = await connectOAuthIntegration(
          toolkitName,
          displayName,
          account?.account_label,
          (success: boolean) => {
            integrationsStore.unmarkPending(toolkitName);
            if (success) trackToolkitConnected(toolkitName, source);
          },
          options?.returnTo,
          account?.id
        );

        if (!result.success) {
          toast.error(result.error || `Failed to connect ${displayName}`);
          if (!result.needsOAuth) {
            integrationsStore.unmarkPending(toolkitName);
          }
        }
      } catch (error) {
        console.error('Unexpected error in connect:', error);
        toast.error(`Unexpected error: ${displayName}`);
        integrationsStore.unmarkPending(toolkitName);
      }
      return;
    }

    const accountId = account?.id ?? integration.accounts[0]?.id;
    if (!accountId) {
      toast.error('Cannot disconnect: integration account ID not found');
      return;
    }

    try {
      const result = await disconnectMutation.mutateAsync(accountId);
      if (result.success) {
        toast.success(`${displayName} disconnected successfully`);
      } else {
        toast.error(result.message || `Failed to disconnect ${displayName}`);
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error(`Failed to disconnect ${displayName}`);
    }
  };

  return { handleAction };
}
